/**
 * Tetris sound effects (C) — synthesised with the Web Audio API, same shape
 * as the other arcade modules (unlock/play/setMuted/isMuted/dispose).
 */
const SILENT = 0.0001;

function defaultAudioContextFactory() {
    if (typeof window === 'undefined') {
        return null;
    }
    const Ctor = window.AudioContext || window.webkitAudioContext;
    return Ctor ? new Ctor() : null;
}

export function createTetrisSound({ audioContextFactory = defaultAudioContextFactory, muted = false } = {}) {
    let ctx = null;
    let triedFactory = false;
    let isMuted = !!muted;
    let disposed = false;

    const now = () => (ctx && typeof ctx.currentTime === 'number' ? ctx.currentTime : 0);

    function envelope(gainValue, start, duration) {
        const g = ctx.createGain();
        g.gain.setValueAtTime(gainValue, start);
        g.gain.exponentialRampToValueAtTime(SILENT, start + duration);
        g.connect(ctx.destination);
        return g;
    }

    function tone({
        freq, endFreq = null, type = 'square', duration = 0.1, gain = 0.12, at = 0,
    }) {
        const start = now() + at;
        const osc = ctx.createOscillator();
        osc.type = type;
        osc.frequency.setValueAtTime(freq, start);
        if (endFreq) {
            osc.frequency.exponentialRampToValueAtTime(endFreq, start + duration);
        }
        osc.connect(envelope(gain, start, duration));
        osc.start(start);
        osc.stop(start + duration + 0.02);
    }

    function sequence(freqs, { type = 'square', duration = 0.09, gap = 0.08, gain = 0.14 } = {}) {
        freqs.forEach((freq, i) => tone({
            freq, type, duration, gain, at: i * gap,
        }));
    }

    const effects = {
        move: () => tone({
            freq: 200, type: 'square', duration: 0.03, gain: 0.05,
        }),
        rotate: () => tone({
            freq: 400, endFreq: 600, type: 'square', duration: 0.06, gain: 0.08,
        }),
        lock: () => tone({
            freq: 80, type: 'sawtooth', duration: 0.08, gain: 0.14,
        }),
        lineClear: (event) => {
            if (event.count >= 4) {
                sequence([523.25, 659.25, 783.99, 1046.5, 1318.5], { duration: 0.1, gap: 0.09 });
            } else {
                sequence([659.25, 880], { duration: 0.08, gap: 0.08 });
            }
        },
        levelUp: () => sequence([440, 554.37, 659.25, 880], { duration: 0.08, gap: 0.07, type: 'triangle' }),
        start: () => sequence([523.25, 659.25, 783.99], { duration: 0.12, gap: 0.12 }),
        gameOver: () => sequence([330, 262, 196], { type: 'sawtooth', duration: 0.22, gap: 0.2, gain: 0.18 }),
    };

    return {
        unlock() {
            if (disposed) {
                return;
            }
            if (!ctx && !triedFactory) {
                triedFactory = true;
                ctx = audioContextFactory() || null;
            }
            if (ctx && ctx.state !== 'running' && typeof ctx.resume === 'function') {
                const resumed = ctx.resume();
                if (resumed && typeof resumed.catch === 'function') {
                    resumed.catch(() => {});
                }
            }
        },
        play(event) {
            if (!ctx || disposed || isMuted || !event) {
                return;
            }
            const effect = effects[event.type];
            if (effect) {
                effect(event);
            }
        },
        setMuted(value) {
            isMuted = !!value;
        },
        isMuted() {
            return isMuted;
        },
        dispose() {
            if (ctx && typeof ctx.close === 'function') {
                const closed = ctx.close();
                if (closed && typeof closed.catch === 'function') {
                    closed.catch(() => {});
                }
            }
            disposed = true;
        },
    };
}
